/**
 * How is this metal panel roof finished?
 *
 * Asked before an AG panel or R panel estimate is generated. Two questions, and
 * both of them change what is bought rather than how much of it: the finish
 * runs through every trim, cap and screw on the order, and furring strips are
 * three lines of their own plus a different length of panel screw.
 *
 * See packages/core/src/exposedFastener.ts for the rules and the quotes behind
 * them. Deliberately the same shape as FlatRoofQuestions — a rep who has met
 * one should not have to learn the other.
 */

import {
  FINISH_LABELS,
  PANEL_FINISHES,
  type ExposedFastenerAnswers,
  type PanelFinish,
} from "@roof-hawk/core";

import { t } from "../../i18n";

const FINISH_HINT: Record<PanelFinish, string> = {
  painted:
    "Panels, trim, ridge cap and stitch screws all color matched, plus touch-up paint.",
  galvalume:
    "Bare galvalume throughout — trim and fasteners to match, and no touch-up paint.",
};

export function MetalPanelQuestions({
  answers,
  onChange,
}: {
  answers: ExposedFastenerAnswers;
  onChange: (answers: ExposedFastenerAnswers) => void;
}) {
  const set = <K extends keyof ExposedFastenerAnswers>(
    key: K,
    value: ExposedFastenerAnswers[K],
  ) => onChange({ ...answers, [key]: value });

  return (
    <div className="flatq">
      <h3>{t("How is this roof finished?")}</h3>

      <label className="flatq-row">
        <span>{t("Finish")}</span>
        <select
          value={answers.finish}
          onChange={(e) => set("finish", e.target.value as PanelFinish)}
        >
          {PANEL_FINISHES.map((finish) => (
            <option key={finish} value={finish}>
              {t(FINISH_LABELS[finish])}
            </option>
          ))}
        </select>
        <span className="flatq-hint">{t(FINISH_HINT[answers.finish])}</span>
      </label>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.furringStrips}
          onChange={(e) => set("furringStrips", e.target.checked)}
        />
        {t("Furring strips over the existing roof")}
      </label>

      {/* Outside the label, because it describes what ticking the box did
          rather than what the box is — the same reason the flat roof form puts
          its second-material hint outside. */}
      <p className="flatq-hint">
        {answers.furringStrips
          ? t(
              'Adds the strips, the framing nails and the labor to install them, and the panels take a 2" screw.',
            )
          : t(
              'Panels fasten straight to the deck with a 1 1/2" screw. No strips, nails or furring labor.',
            )}
      </p>
    </div>
  );
}
